import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
  ClipboardEventHandler,
} from 'react';
import { useIntl } from 'react-intl';

import { useDateTimeRangePickerValidation } from './useDateTimeRangePickerValidation.js';
import type { TimeValue } from '../../../core/types/time.js';
import { mapToTimeframeV2 } from '../../forms-core/time/timeParser.js';
import { SelectedOverCalendarContext } from '../contexts/SelectedOverCalendarContext.js';
import {
  removePrecisionFromTimeFrameV2,
  isEqualTimeValue,
  removePrecisionFromTimeValue,
} from '../helpers.js';
import type { DateTimeInputRef, DateTimeRangePickerProps } from '../types.js';

interface UseDateTimeRangePickerProps
  extends Pick<
    DateTimeRangePickerProps,
    | 'value'
    | 'defaultValue'
    | 'placeholder'
    | 'readOnly'
    | 'min'
    | 'max'
    | 'precision'
    | 'onChange'
  > {
  touchedContainerRef: RefObject<HTMLElement | null>;
  validationInputRef: RefObject<DateTimeInputRef | null>;
}

/**
 * A hook that provides the default functionality for the DateTimeRange picker
 * @internal
 */
export const useDateTimeRangePicker = (props: UseDateTimeRangePickerProps) => {
  const {
    value,
    defaultValue,
    placeholder,
    readOnly,
    min,
    max,
    precision = 'minutes',
    onChange,
    touchedContainerRef,
    validationInputRef,
  } = props;
  const { formatMessage } = useIntl();

  const {
    selectedOverCalendar,
    setCalendarOnChangeFlag,
    setSelectedOverCalendar,
    calendarOnChangeFlag,
  } = useContext(SelectedOverCalendarContext);

  // remove the precision of the value
  const precisionRemovedValue = removePrecisionFromTimeFrameV2(
    value,
    precision,
  );
  const timeframeValue = precisionRemovedValue
    ? mapToTimeframeV2(precisionRemovedValue)
    : null;

  // remove the precision of the defaultValue
  const precisionRemovedDefaultValue = removePrecisionFromTimeFrameV2(
    defaultValue,
    precision,
  );
  const timeframeDefaultValue = precisionRemovedDefaultValue
    ? mapToTimeframeV2(precisionRemovedDefaultValue)
    : null;

  const [valueFrom, setValueFrom] = useState<TimeValue | null>(
    timeframeValue?.from ?? timeframeDefaultValue?.from ?? null,
  );
  const [valueTo, setValueTo] = useState<TimeValue | null>(
    timeframeValue?.to ?? timeframeDefaultValue?.to ?? null,
  );

  // hook to handle from and to value states as well as the combined error (e.g. invalid order)
  const {
    reevaluate,
    evaluate,
    onBlurTouched,
    onError,
    errorTo,
    errorFrom,
    validate,
  } = useDateTimeRangePickerValidation(
    touchedContainerRef,
    validationInputRef,
    valueFrom,
    valueTo,
    {
      min,
      max,
      precision,
    },
  );

  const controlledValueUpdate = useRef(true);

  const localizedPlaceholder = useMemo(
    () => ({
      from:
        placeholder?.from ??
        formatMessage({
          defaultMessage: 'Start time',
          id: 'gnKOHMjpyzk6hNcF',
          description: 'Start of timeframe',
        }),
      to:
        placeholder?.to ??
        formatMessage({
          defaultMessage: 'End time',
          id: 'sGxf5yRVhyC8Fpo/',
          description: 'End of timeframe',
        }),
    }),
    [formatMessage, placeholder?.from, placeholder?.to],
  );

  // for non null values, put the from/to value as `<TimeValue>/<TimeValue>` into the clipboard
  const onCopyCapture: ClipboardEventHandler = useCallback(
    (event) => {
      if (valueFrom === null || valueTo === null) {
        return;
      }

      navigator.clipboard.writeText(`${valueFrom.value}/${valueTo.value}`);
      event.preventDefault();
      event.stopPropagation();
    },
    [valueFrom, valueTo],
  );

  // clipboard paste handler. apply clipboard content if formatted correctly
  const onPasteCapture: ClipboardEventHandler = useCallback(
    (e) => {
      if (readOnly) {
        return;
      }
      const data = e.clipboardData?.getData('text/plain');
      if (!data) {
        return;
      }

      // if the clipboard content is of format `<TimeValue>/<TimeValue>`, apply them.
      const [nextFrom, nextTo] = data
        .trim()
        .split('/')
        .map((input) => evaluate(input));

      if (nextFrom && nextTo) {
        setValueFrom(nextFrom);
        setValueTo(nextTo);
        e.preventDefault();
      }
    },
    [evaluate, readOnly],
  );

  // call onChange prop on from/to value change
  useEffect(() => {
    // Don't call the onChange when the changes coming in are actually
    // from a controlled value change.
    if (controlledValueUpdate.current) {
      controlledValueUpdate.current = false;
      return;
    }
    // if both values from & to are null, return null.
    if (valueFrom === null && valueTo === null) {
      onChange?.(null);
      return;
    }

    // only fire on change if a from and a to value is available.
    if (valueFrom !== null && valueTo !== null) {
      // the first onChange after selecting over the calendar is intended
      if (
        calendarOnChangeFlag &&
        !!setCalendarOnChangeFlag &&
        valueTo.absoluteDate
      ) {
        setCalendarOnChangeFlag(false);
      }

      if (selectedOverCalendar && !calendarOnChangeFlag) {
        setSelectedOverCalendar?.(false);
      }

      props.onChange?.({
        from: valueFrom,
        to: valueTo,
      });
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(valueFrom), JSON.stringify(valueTo)]);

  // update internal from and to state change on prop.value change
  useEffect(() => {
    // ignore if uncontrolled
    if (value === undefined) {
      return;
    }
    const timeframeValue = value ? mapToTimeframeV2(value) : value;
    // update internal states, only fire if value differs
    const nextFrom = timeframeValue?.from ?? null;
    if (!isEqualTimeValue(valueFrom, nextFrom)) {
      controlledValueUpdate.current = true;
      setValueFrom(nextFrom);
    }
    const nextTo = timeframeValue?.to ?? null;
    if (!isEqualTimeValue(valueTo, nextTo)) {
      controlledValueUpdate.current = true;
      setValueTo(nextTo);
    }
    // We only want to update the state for changed values to properly distinguish
    // between controlled and uncontrolled, this will be refactored in the time-
    // picking improvement efforts, APPDEV-10330.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return {
    placeholder: localizedPlaceholder,
    value: {
      from: valueFrom,
      to: valueTo,
    },
    onChange: {
      from: (value: TimeValue | null) =>
        setValueFrom(
          reevaluate(removePrecisionFromTimeValue(value, precision)),
        ),
      to: (value: TimeValue | null) =>
        setValueTo(reevaluate(removePrecisionFromTimeValue(value, precision))),
    },
    onCopyCapture,
    onPasteCapture,
    onBlurTouched,
    onError,
    errorFrom,
    errorTo,
    validate,
  };
};
